<base-site-layout
    [heading]="(headingPrefix | translate) + ' — ' + companyCodeLabel"
    [headerIcon]="icon"
    [isDialog]="true"
    (dialogCloseClicked)="close()"
>
    @if (isOrphan) {
        <div class="alert alert-warning d-flex align-items-start gap-2 mb-3">
            <em class="fas fa-triangle-exclamation pt-1"></em>
            <div>
                <strong>{{ 'Buchungskreis nicht mehr zugewiesen' | translate }}</strong>
                <div class="small">
                    {{
                        'Dieser Buchungskreis wurde von der Einkäufergruppe entfernt. Bestehende Zuordnungen können hier aufgeräumt werden; neue lassen sich erst nach erneuter Zuweisung auf Kopfebene anlegen.'
                            | translate
                    }}
                </div>
            </div>
        </div>
    }

    @if (saveError(); as error) {
        <div class="alert alert-danger d-flex align-items-start gap-2 mb-3" role="alert">
            <em class="fas fa-circle-exclamation pt-1"></em>
            <div>
                <strong>{{ 'Speichern fehlgeschlagen' | translate }}</strong>
                <div class="small">{{ error }}</div>
            </div>
        </div>
    }

    <div class="card mg-panel">
        <div class="card-header bg-light d-flex align-items-center gap-2">
            <em class="fas fa-building"></em>
            <span class="fw-semibold">{{ companyCodeLabel }}</span>
        </div>
        <div class="card-body d-flex flex-column gap-2">
            <div class="d-flex gap-2">
                <input
                    type="search"
                    class="form-control form-control-sm flex-grow-1"
                    [formControl]="filterControl"
                    [placeholder]="'Filtern…' | translate"
                />
                @if (!isOrphan) {
                    <button
                        type="button"
                        class="btn btn-sm btn-primary text-nowrap"
                        [disabled]="!canSelectAllVisibleFree()"
                        (click)="selectAllVisibleFree()"
                        [title]="
                            'Alle aktuell sichtbaren freien Warengruppen zur Hinzufügen-Auswahl ergänzen' | translate
                        "
                    >
                        {{ 'Alle Auswählen' | translate }}
                    </button>
                }
            </div>

            <div class="mg-list">
                @if (loading()) {
                    <div class="text-muted fst-italic small py-3 text-center">
                        {{ 'Warengruppen werden geladen...' | translate }}
                    </div>
                } @else if (items().length === 0) {
                    <div class="text-muted fst-italic small py-3 text-center">
                        {{ 'Keine Warengruppen verfügbar.' | translate }}
                    </div>
                } @else {
                    @for (item of items(); track item.materialGroup.id) {
                        @let selected = isSelected(item.materialGroup.id);
                        @let pendingAdd = item.status === 'free' && selected;
                        @let pendingRemove = item.status === 'own' && !selected;
                        <label
                            class="mg-row"
                            [class.mg-row--blocked]="item.status === 'blocked'"
                            [class.mg-row--own]="item.status === 'own' && selected"
                            [class.mg-row--pending-add]="pendingAdd"
                            [class.mg-row--pending-remove]="pendingRemove"
                        >
                            <input
                                type="checkbox"
                                [checked]="selected"
                                [disabled]="item.status === 'blocked' || saving()"
                                (change)="toggle(item)"
                            />
                            @if (item.status === 'blocked') {
                                <em class="fas fa-lock text-muted"></em>
                            } @else if (pendingRemove) {
                                <em class="far fa-trash-can text-danger"></em>
                            } @else if (pendingAdd) {
                                <em class="fas fa-plus text-success"></em>
                            } @else {
                                <em class="fas fa-sitemap text-muted"></em>
                            }
                            <span class="fw-semibold font-monospace">{{ item.materialGroup.code }}</span>
                            <span class="text-muted">–</span>
                            <span class="flex-grow-1">
                                {{ item.materialGroup.primaryName || item.materialGroup.code }}
                            </span>

                            @if (pendingAdd) {
                                <span class="badge text-bg-success">
                                    <em class="fas fa-plus pe-1"></em>
                                    {{ 'wird hinzugefügt' | translate }}
                                </span>
                            } @else if (pendingRemove) {
                                <span class="badge text-bg-danger">
                                    <em class="far fa-trash-can pe-1"></em>
                                    {{ 'wird entfernt' | translate }}
                                </span>
                            } @else if (item.status === 'own') {
                                <span class="badge text-bg-secondary">
                                    <em class="fas fa-check pe-1"></em>
                                    {{ 'bereits zugewiesen' | translate }}
                                </span>
                            } @else if (item.status === 'blocked' && item.owner) {
                                <span
                                    class="badge text-bg-warning"
                                    [title]="
                                        ('belegt durch' | translate) + ' ' + item.owner.code + ' – ' + item.owner.name
                                    "
                                >
                                    <em class="fas fa-lock pe-1"></em>
                                    {{ item.owner.code }} – {{ item.owner.name }}
                                </span>
                            }
                        </label>
                    }
                }
            </div>
        </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mt-3 gap-2 flex-wrap">
        <div class="small text-muted">
            @if (hasChanges()) {
                @if (addIds().length > 0) {
                    <span class="me-3">
                        <em class="fas fa-plus pe-1 text-success"></em>
                        {{ addIds().length }} {{ 'hinzufügen' | translate }}
                    </span>
                }
                @if (removeIds().length > 0) {
                    <span>
                        <em class="far fa-trash-can pe-1 text-danger"></em>
                        {{ removeIds().length }} {{ 'entfernen' | translate }}
                    </span>
                }
            } @else {
                <span>{{ 'Keine Änderungen.' | translate }}</span>
            }
        </div>
        <div class="d-flex gap-2">
            <button type="button" class="btn btn-outline-secondary" (click)="close()" [disabled]="saving()">
                <em class="fas fa-xmark pe-1"></em>
                {{ 'Abbrechen' | translate }}
            </button>
            <button type="button" class="btn btn-primary" [disabled]="!canSave()" (click)="save()">
                <em class="fas fa-check pe-1"></em>
                {{ 'Speichern' | translate }}
            </button>
        </div>
    </div>
</base-site-layout>
